import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import { User } from '@supabase/supabase-js';
import { LocalizedAuthService } from '../../services/localizedAuthService';
import { SupportedLanguage } from '../../i18n';
import FCMService from '../../services/fcmService';

// 類型定義
interface Profile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  bio: string | null;
  created_at?: string;
  updated_at?: string;
  username: string | null;
  points: number;
  vip_expires_at: string | null;
  is_vip_active: boolean;
  last_login_at: string | null;
  login_streak: number;
  region: string | null;
  fcm_token?: string | null;
  fcm_token_updated_at?: string;
  // 新增：社交账号（JSONB）
  // 结构建议：{ platformKey: { id?: string, display_name?: string|null, url?: string|null, verified?: boolean, visibility?: string } }
  social_accounts?: Record<string, any> | null;
  // 管理员标识
  is_admin?: boolean;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isProcessingOAuth: boolean;
  oauthRedirectTarget: string | null;
  returnTo: string | null;
}

// 初始狀態
const initialState: AuthState = {
  user: null,
  profile: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  isProcessingOAuth: false,
  oauthRedirectTarget: null,
  returnTo: null,
};

// 異步操作：獲取當前用戶
export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async (_, { rejectWithValue, dispatch }) => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return rejectWithValue(sessionError.message);
    } else if (session) {
      // 有效session，获取用户信息
    } else {
      // 没有找到有效session
    }
    
    // 获取用户信息
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      // 如果是session missing错误，这是正常的，返回null
      if (error.message.includes('session') || error.message.includes('missing')) {
        console.log('ℹ️ 用户未登录 (无session)');
        return null;
      }
      console.error('❌ 获取用户信息失败:', error);
      throw error;
    }
    
    if (user) {
      console.log('✅ 获取到用户信息:', {
        id: user.id,
        email: user.email,
        provider: user.app_metadata?.provider,
        emailConfirmed: user.email_confirmed_at ? '已确认' : '未确认'
      });
      
      // 如果是Google登录用户，加载或创建profile
      // 加载用户 profile（对所有 provider 都尝试加载），并等待完成以避免 UI race
      try {
        await dispatch(getUserProfile(user.id)).unwrap();
      } catch (e) {
        console.warn('⚠️ 获取用户资料失败 (非阻断):', e);
      }
    }
    
    return user;
    } catch (error: any) {
      console.warn('❌ getCurrentUser失败:', error);
      return rejectWithValue(error.message);
  }
});

// 異步操作：獲取用戶個人資料
export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log('🔍 正在查找用户profile:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // 如果是profile不存在的错误，尝试创建一个新的profile
        if (error.code === 'PGRST116') {
          console.log('📝 Profile不存在，正在创建新profile...');
          
          // 获取用户信息用于创建profile
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          
          const user = userData.user;
          if (!user) throw new Error('Unable to get user information');
          
          // 创建新的profile记录 - 只包含数据库中存在的基本字段
          const newProfile = {
            id: userId,
            display_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
            avatar_url: user.user_metadata?.avatar_url || null,
            phone: null,
            bio: null,
            username: null,
            points: 0,
            vip_expires_at: null,
            is_vip_active: false,
            last_login_at: new Date().toISOString(),
            login_streak: 1,
            region: null,
          };
          
          console.log('📝 创建新profile:', newProfile);
          
          const { data: createdProfile, error: createError } = await supabase
            .from('profiles')
            .insert([newProfile])
            .select()
            .single();
            
          if (createError) {
            console.error('❌ 创建profile失败:', createError);
            throw createError;
          }
          
          console.log('✅ 成功创建新profile:', createdProfile);
          
          // 初始化FCM token收集
          try {
            await FCMService.initialize();
            const token = await FCMService.getToken();
            if (token) {
              await FCMService.saveUserToken(userId, token);
              console.log('FCM token已保存:', token.substring(0, 20) + '...');
            }
          } catch (fcmError) {
            console.warn('FCM token收集失败:', fcmError);
            // 不阻塞用户登录流程
          }
          
          return createdProfile as Profile;
        }
        
        console.error('❌ 获取profile失败:', error);
        throw error;
      }
      
      console.log('✅ 找到现有profile:', data);
      
      // 初始化FCM token收集
      try {
        await FCMService.initialize();
        const token = await FCMService.getToken();
        if (token) {
          await FCMService.saveUserToken(userId, token);
          console.log('FCM token已保存:', token.substring(0, 20) + '...');
        }
      } catch (fcmError) {
        console.warn('FCM token收集失败:', fcmError);
        // 不阻塞用户登录流程
      }
      
      return data as Profile;
    } catch (error: any) {
      console.error('❌ getUserProfile失败:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：用戶註冊（支持国际化）
export const signUp = createAsyncThunk(
  'auth/signUp',
  async (
    { 
      email, 
      password, 
      displayName, 
      username,
      region,
      referralCode,
      language
    }: { 
      email: string; 
      password: string; 
      displayName: string;
      username?: string;
      region?: string;
      referralCode?: string;
      language?: SupportedLanguage;
    },
    { rejectWithValue }
  ) => {
    try {
      // 使用本地化认证服务进行注册
      const { data, userLanguage } = await LocalizedAuthService.signUpWithLocalizedEmail({
        email,
        password,
        username,
        language,
        region,
        referralCode
      });
      
      const user = data.user;
      console.log('註冊成功，用戶ID:', user?.id, '使用语言:', userLanguage);
      
      if (!user) {
        console.error('註冊API成功但未返回用戶');
        throw new Error('註冊失敗，未返回用戶');
      }
      
      // 成功注册，返回本地化结果
      console.log('用戶註冊成功 - 等待邮件验证，语言:', userLanguage);
      return { 
        user, 
        needsEmailVerification: true,
        email: email,
        userLanguage
      };
    } catch (error: any) {
      console.error('註冊過程中捕獲錯誤:', error);
      return rejectWithValue(error.message || '註冊失敗，請稍後重試');
    }
  }
);

// 異步操作：用戶登錄
export const signIn = createAsyncThunk(
  'auth/signIn',
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      console.log('开始登录，邮箱:', email);
      
      // 执行登录
      console.log('执行登录验证...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      const user = data.user;

      if (error) {
        console.error('登录失败，详细错误:', error);
        throw error;
      }
      
      console.log('登录成功，用户ID:', user?.id);
      
      if (user) {
        console.log('加载用户资料...');
        dispatch(getUserProfile(user.id));
      }
      
      return user;
    } catch (error: any) {
      console.error('登录过程中捕获到错误:', error);
      return rejectWithValue(error.message || '登录失败，请检查邮箱和密码');
    }
  }
);

// 異步操作：用戶登出
export const signOut = createAsyncThunk('auth/signOut', async (_, { rejectWithValue }) => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return null;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// 異步操作：更新用戶資料
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (
    { userId, profileData }: { userId: string; profileData: Partial<Profile> },
    { rejectWithValue }
  ) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data as Profile;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：更新用户名
export const updateUsername = createAsyncThunk(
  'auth/updateUsername',
  async (
    { userId, username }: { userId: string; username: string },
    { rejectWithValue }
  ) => {
    try {
      console.log('更新用户名:', { userId, username });
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ username })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('更新用户名失败:', error);
        throw error;
      }

      console.log('用户名更新成功:', data);
      return data as Profile;
    } catch (error: any) {
      console.error('更新用户名异常:', error);
      return rejectWithValue(error.message || '更新用户名失败');
    }
  }
);

// 異步操作：增强的用戶登錄（支持邮箱或用户名）
export const signInWithIdentifier = createAsyncThunk(
  'auth/signInWithIdentifier',
  async (
    { identifier, password }: { identifier: string; password: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      console.log('开始登录，标识符:', identifier);
      
      let email = identifier;
      
      // 如果输入的不是邮箱格式，尝试通过用户名查找邮箱
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(identifier)) {
        console.log('检测到用户名登录，查找对应邮箱...');
        
        const { data: userData, error: findError } = await supabase
          .rpc('find_user_by_email_or_username', { identifier });
        
        console.log('数据库查找结果:', { userData, findError });
        
        if (findError) {
          console.error('查找用户失败:', findError);
          throw new Error(`Database query error: ${findError.message}`);
        }
        
        if (!userData || userData.length === 0) {
          throw new Error(`Username "${identifier}" does not exist`);
        }
        
        email = userData[0].email;
        console.log('找到对应邮箱:', email);
      }
      
      // 执行登录
      console.log('执行登录验证...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      const user = data.user;

      if (error) {
        console.error('登录失败，详细错误:', error);
        // 根据错误类型提供更友好的错误信息
        if (error.message.includes('Invalid login credentials')) {
          // throw a canonical English message so UI code can detect and i18n it
          throw new Error('Invalid login credentials');
        }
        throw error;
      }
      
      console.log('登录成功，用户ID:', user?.id);
      
      if (user) {
        console.log('加载用户资料...');
        // 异步加载用户资料，不等待结果，避免阻塞登录流程
        dispatch(getUserProfile(user.id)).catch((error) => {
          console.warn('获取用户资料失败:', error);
        });
      }
      
      return user;
    } catch (error: any) {
      console.warn('登录过程中捕获到错误:', error);
      return rejectWithValue(error.message || '登录失败，请检查用户名/邮箱和密码');
    }
  }
);

// 創建Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.profile = null;
      state.isAuthenticated = false;
      state.error = null;
      state.loading = false;
      state.isProcessingOAuth = false;
    },
    setOAuthProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessingOAuth = action.payload;
    },
    setOAuthRedirectTarget: (state, action: PayloadAction<string | null>) => {
      state.oauthRedirectTarget = action.payload;
    },
    setReturnTo: (state, action: PayloadAction<string | null>) => {
      state.returnTo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // getCurrentUser
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = !!action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      })
      // getUserProfile
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
          // no-op: keep profile only in Redux; avoid localStorage fallback (avoid stale/unsafe cache)
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // signUp
      .addCase(signUp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // signIn
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      })
      // signOut
      .addCase(signOut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateProfile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // signInWithIdentifier
      .addCase(signInWithIdentifier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithIdentifier.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(signInWithIdentifier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      })
      // updateUsername
      .addCase(updateUsername.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUsername.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateUsername.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearAuth, setOAuthProcessing, setOAuthRedirectTarget, setReturnTo } = authSlice.actions;
export default authSlice.reducer;